import {
  Badge,
  Box,
  Button,
  Card,
  CardBody,
  CardHeader,
  Flex,
  FormControl,
  FormLabel,
  HStack,
  Heading,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Portal,
  Radio,
  RadioGroup,
  Text,
  Textarea,
  Tooltip,
  useColorModeValue,
  useDisclosure,
  VStack,
  Tabs, TabList, Tab, TabPanels, TabPanel,
} from '@chakra-ui/react'
import { CheckIcon, CloseIcon } from '@chakra-ui/icons'
import { useCallback, useMemo, useRef, useState, type RefObject } from 'react'
import {
  FiCopy,
  FiDownload,
  FiEdit,
  FiMail,
  FiPlus,
  FiPlusCircle,
  FiTrash2,
  FiUpload,
} from 'react-icons/fi'
import { useTranslation } from 'react-i18next'
import { UserInvitationsTab } from './UserInvitationsTab'
import { DataTable } from '@/components/common/DataTable'
import type { ActionButton, ColumnConfig } from '@/components/common/DataTable'
import { DropdownSelect } from '@/components/common/DropdownSelect'
import { MainnetOnlyNotice } from '@/components/common/MainnetOnlyNotice'
import { RefreshIconButton } from '@/components/common/RefreshIconButton'
import {
  triggerBrowserDownload,
} from '@/services/admin/baseService'
import { merchantSignupInviteService } from '@/services/admin/merchantSignupInviteService'
import {
  useCreateMerchantSignupInviteEntry,
  useDeleteMerchantSignupInviteEntry,
  useGenerateMerchantSignupInviteEntries,
  useImportMerchantSignupInviteEntries,
  useMerchantSignupInviteEntries,
  useUpdateMerchantSignupInviteEntry,
} from '@/hooks/useAdminData'
import { useNetworkEnvironment } from '@/hooks/useNetworkEnvironment'
import { useToast } from '@/hooks/useToast'
import type {
  MerchantSignupAccessEntry,
  MerchantSignupAccessGenerateResponse,
  MerchantSignupAccessImportError,
  SignupEntryType,
} from '@/types'

type MerchantSignupInvitesTabProps = {
  cardBg: string
}

type EntryStatusFilter = 'all' | 'active' | 'disabled'

type EntryFormState = {
  value: string
  label: string
  status: 'active' | 'disabled'
  expiresAt: string
}

type ImportFormState = {
  values: string
  status: 'active' | 'disabled'
  expiresAt: string
}

type GenerateFormState = {
  count: string
}

type ImportResultState = {
  createdCount: number
  updatedCount: number
  skippedCount: number
  errors: MerchantSignupAccessImportError[]
} | null

type GenerateResultState = MerchantSignupAccessGenerateResponse | null

const DEFAULT_GENERATE_COUNT = 20
const MAX_GENERATE_COUNT = 1000

const formatDateTime = (value?: string | null) => {
  if (!value) {
    return '-'
  }
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) {
    return value
  }
  return parsed.toLocaleString()
}

const toDateTimeInputValue = (value?: string | null) => {
  if (!value) {
    return ''
  }
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) {
    return ''
  }
  const pad = (input: number) => String(input).padStart(2, '0')
  return `${parsed.getFullYear()}-${pad(parsed.getMonth() + 1)}-${pad(parsed.getDate())}T${pad(parsed.getHours())}:${pad(parsed.getMinutes())}`
}

const normalizeEntryInput = (entryType: SignupEntryType, value: string) =>
  entryType === 'invite_code' ? value.toUpperCase() : value.toLowerCase()

const escapeCsvValue = (value: string) => `"${value.replace(/"/g, '""')}"`

const buildGeneratedInviteCsv = (result: MerchantSignupAccessGenerateResponse) => {
  const rows = [
    ['batch_no', 'label', 'code'],
    ...result.codes.map((code) => [result.batchNo, result.label, code]),
  ]

  return rows
    .map((row) => row.map(escapeCsvValue).join(','))
    .join('\n')
}

export function MerchantSignupInvitesTab({ cardBg }: MerchantSignupInvitesTabProps) {
  const { t } = useTranslation()
  const { currentEnv } = useNetworkEnvironment()
  const refreshContainerRef = useRef<HTMLDivElement>(null)
  if (currentEnv !== 'mainnet') return <MainnetOnlyNotice />
  return <Tabs isLazy lazyBehavior="unmount">
    <Flex align="center" gap={2}>
      <TabList flex={1} minW={0}><Tab fontSize="sm">{t('userInvitations.official')}</Tab><Tab fontSize="sm">{t('userInvitations.title')}</Tab></TabList>
      <Box ref={refreshContainerRef} flexShrink={0} />
    </Flex>
    <TabPanels><TabPanel px={0}><OfficialSignupInvitesTab cardBg={cardBg} refreshContainerRef={refreshContainerRef} /></TabPanel>
      <TabPanel px={0}><UserInvitationsTab cardBg={cardBg} refreshContainerRef={refreshContainerRef} /></TabPanel></TabPanels>
  </Tabs>
}

function OfficialSignupInvitesTab({ cardBg, refreshContainerRef }: MerchantSignupInvitesTabProps & {
  refreshContainerRef: RefObject<HTMLDivElement>;
}) {
  const { t } = useTranslation()
  const toast = useToast()
  const { currentEnv } = useNetworkEnvironment()
  const isMainnet = currentEnv === 'mainnet'
  const copyHoverBg = useColorModeValue('blue.50', 'whiteAlpha.200')
  const copyValueTextColor = useColorModeValue('gray.800', 'gray.100')

  const [filters, setFilters] = useState<{
    entryType: SignupEntryType
    status: EntryStatusFilter
    limit: number
  }>({
    entryType: 'invite_code',
    status: 'all',
    limit: 100,
  })

  const queryParams = useMemo(
    () => ({
      entryType: filters.entryType,
      status: filters.status === 'all' ? undefined : filters.status,
      limit: filters.limit,
    }),
    [filters]
  )

  const {
    data,
    isLoading,
    isFetching,
    refetch,
  } = useMerchantSignupInviteEntries(queryParams, { enabled: isMainnet })
  const createMutation = useCreateMerchantSignupInviteEntry()
  const updateMutation = useUpdateMerchantSignupInviteEntry()
  const deleteMutation = useDeleteMerchantSignupInviteEntry()
  const importMutation = useImportMerchantSignupInviteEntries()
  const generateMutation = useGenerateMerchantSignupInviteEntries()

  const {
    isOpen: isEditModalOpen,
    onOpen: openEditModal,
    onClose: closeEditModal,
  } = useDisclosure()
  const {
    isOpen: isImportModalOpen,
    onOpen: openImportModal,
    onClose: closeImportModal,
  } = useDisclosure()
  const {
    isOpen: isGenerateModalOpen,
    onOpen: openGenerateModal,
    onClose: closeGenerateModal,
  } = useDisclosure()

  const [editingRow, setEditingRow] = useState<MerchantSignupAccessEntry | null>(null)
  const [form, setForm] = useState<EntryFormState>({
    value: '',
    label: '',
    status: 'active',
    expiresAt: '',
  })
  const [importForm, setImportForm] = useState<ImportFormState>({
    values: '',
    status: 'active',
    expiresAt: '',
  })
  const [generateForm, setGenerateForm] = useState<GenerateFormState>({
    count: String(DEFAULT_GENERATE_COUNT),
  })
  const [importResult, setImportResult] = useState<ImportResultState>(null)
  const [generateResult, setGenerateResult] = useState<GenerateResultState>(null)
  const [isExporting, setIsExporting] = useState(false)

  const rows = data?.entries ?? []
  const currentEntryType = filters.entryType
  const isInviteType = currentEntryType === 'invite_code'
  const isSaving = createMutation.isPending || updateMutation.isPending

  const resetForm = useCallback((row?: MerchantSignupAccessEntry | null) => {
    setForm({
      value: row?.value ?? '',
      label: row?.label ?? '',
      status: row?.status ?? 'active',
      expiresAt: toDateTimeInputValue(row?.expiresAt),
    })
  }, [])

  const resetImportForm = useCallback(() => {
    setImportForm({
      values: '',
      status: 'active',
      expiresAt: '',
    })
    setImportResult(null)
  }, [])

  const resetGenerateForm = useCallback(() => {
    setGenerateForm({
      count: String(DEFAULT_GENERATE_COUNT),
    })
    setGenerateResult(null)
  }, [])

  const handleCreate = useCallback(() => {
    setEditingRow(null)
    resetForm(null)
    openEditModal()
  }, [openEditModal, resetForm])

  const handleEdit = useCallback((row: MerchantSignupAccessEntry) => {
    setEditingRow(row)
    resetForm(row)
    openEditModal()
  }, [openEditModal, resetForm])

  const handleClose = useCallback(() => {
    closeEditModal()
    setEditingRow(null)
    resetForm(null)
  }, [closeEditModal, resetForm])

  const handleCloseImport = useCallback(() => {
    closeImportModal()
    resetImportForm()
  }, [closeImportModal, resetImportForm])

  const handleOpenGenerate = useCallback(() => {
    resetGenerateForm()
    openGenerateModal()
  }, [openGenerateModal, resetGenerateForm])

  const handleCloseGenerate = useCallback(() => {
    closeGenerateModal()
    resetGenerateForm()
  }, [closeGenerateModal, resetGenerateForm])

  const handleExport = useCallback(async () => {
    try {
      setIsExporting(true)
      const { blob, filename } = await merchantSignupInviteService.exportCsv(queryParams, currentEnv)
      triggerBrowserDownload(blob, filename)
      toast.success({
        title: t('common.export.success'),
        description: t('common.export.downloaded'),
      })
    } catch (error) {
      toast.error({
        title: t('common.export.label'),
        description: error instanceof Error ? error.message : String(error),
      })
    } finally {
      setIsExporting(false)
    }
  }, [currentEnv, queryParams, t, toast])

  const copyTextValue = useCallback(async (value: string) => {
    if (!value) {
      return
    }

    try {
      if (typeof window !== 'undefined' && window.isSecureContext && navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(value)
      } else if (typeof document !== 'undefined') {
        const textarea = document.createElement('textarea')
        textarea.value = value
        textarea.setAttribute('readonly', 'true')
        textarea.style.position = 'absolute'
        textarea.style.left = '-9999px'
        document.body.appendChild(textarea)
        textarea.select()
        document.execCommand('copy')
        document.body.removeChild(textarea)
      } else {
        throw new Error('Clipboard API unavailable')
      }

      toast.success({
        title: t('common.copySuccess'),
      })
    } catch {
      toast.error({
        title: t('common.copyFailed'),
      })
    }
  }, [t, toast])

  const entryTypeOptions = useMemo(
    () => [
      { value: 'invite_code', label: t('systemConfig.signupEntries.entryTypes.inviteCode') },
      { value: 'email_whitelist', label: t('systemConfig.signupEntries.entryTypes.emailWhitelist') },
    ],
    [t]
  )

  const statusOptions = useMemo(
    () => [
      { value: 'all', label: t('common.all') },
      { value: 'active', label: t('common.active') },
      { value: 'disabled', label: t('common.inactive') },
    ],
    [t]
  )

  const modalStatusOptions = useMemo(
    () => [
      { value: 'active', label: t('common.active') },
      { value: 'disabled', label: t('common.inactive') },
    ],
    [t]
  )

  const columns = useMemo<ColumnConfig<MerchantSignupAccessEntry>[]>(() => {
    const baseColumns: ColumnConfig<MerchantSignupAccessEntry>[] = [
      {
        key: 'value',
        title: isInviteType
          ? t('systemConfig.signupEntries.columns.inviteCode')
          : t('systemConfig.signupEntries.columns.email'),
        width: isInviteType ? '20%' : '28%',
        render: (_, row) => (
          <Tooltip label={t('common.copy')} placement="top">
            <Box
              as="button"
              type="button"
              display="block"
              w="full"
              textAlign="left"
              appearance="none"
              px={2}
              py={1}
              bg="transparent"
              border="none"
              color={copyValueTextColor}
              borderRadius="md"
              cursor="pointer"
              transition="background-color 0.15s ease"
              _hover={{ bg: copyHoverBg }}
              _active={{ bg: copyHoverBg }}
              _focus={{ outline: 'none', boxShadow: 'none' }}
              _focusVisible={{
                outline: 'none',
                boxShadow: 'none',
              }}
              onClick={() => void copyTextValue(row.value)}
              title={row.value}
            >
              <Text fontWeight="medium" noOfLines={1} color={copyValueTextColor}>
                {row.value}
              </Text>
            </Box>
          </Tooltip>
        ),
      },
      {
        key: 'label',
        title: t('systemConfig.signupEntries.columns.label'),
        width: isInviteType ? '18%' : '20%',
        render: (_, row) => <Text fontSize="xs">{row.label || '-'}</Text>,
      },
      {
        key: 'status',
        title: t('systemConfig.signupEntries.columns.status'),
        width: '10%',
        align: 'center',
        render: (_, row) => (
          <Badge colorScheme={row.status === 'active' ? 'green' : 'gray'} fontSize="0.65rem">
            {row.status === 'active' ? t('common.active') : t('common.inactive')}
          </Badge>
        ),
      },
    ]

    if (isInviteType) {
      baseColumns.push(
        {
          key: 'usedCount',
          title: t('systemConfig.signupEntries.columns.usage'),
          width: '8%',
          align: 'center',
          render: (_, row) => {
            const isUsed = row.usedCount >= row.maxUses
            return (
              <Tooltip label={`${row.usedCount}/${row.maxUses}`} placement="top">
                <span>
                  {isUsed ? (
                    <CloseIcon color="red.400" boxSize={3} />
                  ) : (
                    <CheckIcon color="green.400" boxSize={3.5} />
                  )}
                </span>
              </Tooltip>
            )
          },
        },
        {
          key: 'usedByUserEmail',
          title: t('systemConfig.signupEntries.columns.usedByUserEmail'),
          width: '18%',
          render: (_, row) => (
            <Text fontSize="xs" noOfLines={1} title={row.usedByUserEmail || ''}>
              {row.usedByUserEmail || '-'}
            </Text>
          ),
        },
      )
    }

    baseColumns.push(
      {
        key: 'expiresAt',
        title: t('systemConfig.signupEntries.columns.expiresAt'),
        width: isInviteType ? '12%' : '16%',
        render: (_, row) => <Text fontSize="2xs">{formatDateTime(row.expiresAt)}</Text>,
        noWrap: true,
      },
      {
        key: 'updatedAt',
        title: t('systemConfig.signupEntries.columns.updatedAt'),
        width: isInviteType ? '12%' : '16%',
        render: (_, row) => <Text fontSize="2xs">{formatDateTime(row.updatedAt)}</Text>,
        noWrap: true,
      },
    )

    return baseColumns
  }, [copyHoverBg, copyTextValue, copyValueTextColor, isInviteType, t])

  const actions = useCallback((row: MerchantSignupAccessEntry): ActionButton[] => ([
    {
      label: t('common.edit'),
      icon: <FiEdit />,
      onClick: () => handleEdit(row),
    },
    {
      label: t('common.delete', { defaultValue: 'Delete' }),
      icon: <FiTrash2 />,
      colorScheme: 'red',
      onClick: () => {
        void (async () => {
          try {
            await deleteMutation.mutateAsync(row.id)
            toast.success({
              title: t('systemConfig.signupEntries.deleteSuccess'),
            })
            await refetch()
          } catch (error) {
            toast.error({
              title: t('systemConfig.signupEntries.deleteFailed'),
              description: error instanceof Error ? error.message : String(error),
            })
          }
        })()
      },
      confirmMessage: t('systemConfig.signupEntries.confirmDelete', {
        value: row.value,
      }),
      disabled: deleteMutation.isPending,
    },
  ]), [deleteMutation, handleEdit, refetch, t, toast])

  const resolveExpiresAt = (value: string): string | null => {
    if (!value.trim()) {
      return null
    }
    const parsed = new Date(value)
    if (Number.isNaN(parsed.getTime())) {
      throw new Error(t('systemConfig.signupEntries.validationExpiresAtInvalid'))
    }
    return parsed.toISOString()
  }

  const handleSave = async () => {
    const normalizedValue = normalizeEntryInput(currentEntryType, form.value.trim())
    if (!normalizedValue) {
      toast.error({
        title: t('systemConfig.signupEntries.validationTitle'),
        description: isInviteType
          ? t('systemConfig.signupEntries.validationInviteRequired')
          : t('systemConfig.signupEntries.validationEmailRequired'),
      })
      return
    }

    let expiresAt: string | null
    try {
      expiresAt = resolveExpiresAt(form.expiresAt)
    } catch (error) {
      toast.error({
        title: t('systemConfig.signupEntries.validationTitle'),
        description: error instanceof Error ? error.message : String(error),
      })
      return
    }

    try {
      if (editingRow) {
        await updateMutation.mutateAsync({
          entryId: editingRow.id,
          payload: {
            value: normalizedValue,
            label: form.label.trim() || null,
            status: form.status,
            expiresAt,
          },
        })
        toast.success({
          title: t('systemConfig.signupEntries.updateSuccess'),
        })
      } else {
        await createMutation.mutateAsync({
          entryType: currentEntryType,
          value: normalizedValue,
          label: form.label.trim() || null,
          status: form.status,
          expiresAt,
        })
        toast.success({
          title: t('systemConfig.signupEntries.createSuccess'),
        })
      }
      await refetch()
      handleClose()
    } catch (error) {
      toast.error({
        title: t('systemConfig.signupEntries.saveFailed'),
        description: error instanceof Error ? error.message : String(error),
      })
    }
  }

  const handleImport = async () => {
    if (!importForm.values.trim()) {
      toast.error({
        title: t('systemConfig.signupEntries.validationTitle'),
        description: t('systemConfig.signupEntries.validationImportRequired'),
      })
      return
    }

    let expiresAt: string | null
    try {
      expiresAt = resolveExpiresAt(importForm.expiresAt)
    } catch (error) {
      toast.error({
        title: t('systemConfig.signupEntries.validationTitle'),
        description: error instanceof Error ? error.message : String(error),
      })
      return
    }

    try {
      const result = await importMutation.mutateAsync({
        entryType: currentEntryType,
        values: importForm.values,
        status: importForm.status,
        expiresAt,
      })
      setImportResult({
        createdCount: result.createdCount,
        updatedCount: result.updatedCount,
        skippedCount: result.skippedCount,
        errors: result.errors,
      })
      if (result.errors.length > 0) {
        toast.warning({
          title: t('systemConfig.signupEntries.importPartial'),
          description: t('systemConfig.signupEntries.importSummary', {
            created: result.createdCount,
            updated: result.updatedCount,
            skipped: result.skippedCount,
          }),
        })
      } else {
        toast.success({
          title: t('systemConfig.signupEntries.importSuccess'),
          description: t('systemConfig.signupEntries.importSummary', {
            created: result.createdCount,
            updated: result.updatedCount,
            skipped: result.skippedCount,
          }),
        })
      }
      await refetch()
      if (result.errors.length === 0) {
        handleCloseImport()
      }
    } catch (error) {
      toast.error({
        title: t('systemConfig.signupEntries.importFailed'),
        description: error instanceof Error ? error.message : String(error),
      })
    }
  }

  const parseGenerateCount = () => {
    const trimmed = generateForm.count.trim()
    const count = trimmed ? Number(trimmed) : DEFAULT_GENERATE_COUNT

    if (!Number.isInteger(count) || count < 1 || count > MAX_GENERATE_COUNT) {
      throw new Error(t('systemConfig.signupEntries.generateCountInvalid', {
        max: MAX_GENERATE_COUNT,
      }))
    }

    return count
  }

  const handleGenerate = async () => {
    let count: number
    try {
      count = parseGenerateCount()
    } catch (error) {
      toast.error({
        title: t('systemConfig.signupEntries.validationTitle'),
        description: error instanceof Error ? error.message : String(error),
      })
      return
    }

    try {
      const result = await generateMutation.mutateAsync({
        count,
      })
      setGenerateResult(result)
      setFilters(prev => ({
        ...prev,
        entryType: 'invite_code',
        status: 'active',
      }))
      toast.success({
        title: t('systemConfig.signupEntries.generateSuccess'),
        description: t('systemConfig.signupEntries.generateResultSummary', {
          count: result.createdCount,
          batchNo: result.batchNo,
        }),
      })
      await refetch()
    } catch (error) {
      toast.error({
        title: t('systemConfig.signupEntries.generateFailed'),
        description: error instanceof Error ? error.message : String(error),
      })
    }
  }

  const handleCopyGeneratedCodes = useCallback(() => {
    if (!generateResult?.codes.length) {
      return
    }

    void copyTextValue(generateResult.codes.join('\n'))
  }, [copyTextValue, generateResult])

  const handleDownloadGeneratedCodes = useCallback(() => {
    if (!generateResult?.codes.length) {
      return
    }

    const csv = buildGeneratedInviteCsv(generateResult)
    triggerBrowserDownload(
      new Blob([`${csv}\n`], { type: 'text/csv;charset=utf-8' }),
      `signup-invite-codes-${generateResult.batchNo}.csv`
    )
  }, [generateResult])

  if (!isMainnet) {
    return <MainnetOnlyNotice />
  }

  return (
    <>
      <VStack spacing={4} align="stretch">
        <Card bg={cardBg}>
          <CardBody py={3}>
            <Flex
              align={{ base: 'stretch', xl: 'center' }}
              justify="space-between"
              gap={3}
              flexWrap={{ base: 'wrap', xl: 'nowrap' }}
            >
              <HStack
                spacing={3}
                align="center"
                flexWrap="wrap"
                flex="1 1 680px"
                minW={0}
                rowGap={2}
              >
                <Heading size="xs" flexShrink={0}>
                  {t('systemConfig.signupEntries.filters')}
                </Heading>
                <FormControl
                  display="flex"
                  alignItems="center"
                  gap={2}
                  w={{ base: '100%', md: 'auto' }}
                  minW={{ base: '100%', md: '300px' }}
                  maxW={{ xl: '300px' }}
                >
                  <FormLabel fontSize="xs" mb={0} whiteSpace="nowrap">
                    {t('systemConfig.signupEntries.columns.entryType')}
                  </FormLabel>
                  <RadioGroup
                    value={filters.entryType}
                    onChange={value =>
                      setFilters(prev => ({
                        ...prev,
                        entryType: value as SignupEntryType,
                      }))
                    }
                  >
                    <HStack spacing={3} align="center" minH="32px" whiteSpace="nowrap">
                      {entryTypeOptions.map(option => (
                        <Radio key={option.value} value={option.value} size="sm">
                          <Text fontSize="xs">{option.label}</Text>
                        </Radio>
                      ))}
                    </HStack>
                  </RadioGroup>
                </FormControl>
                <FormControl
                  display="flex"
                  alignItems="center"
                  gap={2}
                  w={{ base: '100%', sm: '170px' }}
                  minW={{ base: '100%', sm: '170px' }}
                >
                  <FormLabel fontSize="xs" mb={0} whiteSpace="nowrap">
                    {t('systemConfig.signupEntries.columns.status')}
                  </FormLabel>
                  <Box flex="1" minW={0}>
                    <DropdownSelect
                      value={filters.status}
                      options={statusOptions}
                      onChange={value =>
                        setFilters(prev => ({
                          ...prev,
                          status: value as EntryStatusFilter,
                        }))
                      }
                      size="xs"
                    />
                  </Box>
                </FormControl>
                <FormControl
                  display="flex"
                  alignItems="center"
                  gap={2}
                  w={{ base: '100%', sm: '118px' }}
                  minW={{ base: '100%', sm: '118px' }}
                >
                  <FormLabel fontSize="xs" mb={0} whiteSpace="nowrap">
                    {t('systemConfig.signupEntries.columns.limit')}
                  </FormLabel>
                  <Box flex="1" minW={0}>
                    <DropdownSelect
                      value={String(filters.limit)}
                      options={[50, 100, 200, 500].map(limit => ({
                        value: String(limit),
                        label: String(limit),
                      }))}
                      onChange={value =>
                        setFilters(prev => ({
                          ...prev,
                          limit: Number(value),
                        }))
                      }
                      size="xs"
                    />
                  </Box>
                </FormControl>
              </HStack>

              <HStack
                spacing={2}
                justify={{ base: 'flex-start', lg: 'flex-end' }}
                flex="0 0 auto"
                minW={0}
                flexWrap={{ base: 'wrap', md: 'nowrap' }}
                rowGap={2}
              >
                <Portal containerRef={refreshContainerRef}>
                  <RefreshIconButton
                    label={t('common.refresh')}
                    onClick={() => refetch()}
                    isLoading={isFetching}
                  />
                </Portal>
                <Text fontSize="sm" color="gray.500">
                  {t('systemConfig.signupEntries.total', {
                    value: data?.total ?? rows.length,
                  })}
                </Text>
                <Button
                  size="sm"
                  variant="outline"
                  leftIcon={<FiPlusCircle />}
                  onClick={handleOpenGenerate}
                  isLoading={generateMutation.isPending}
                >
                  {t('systemConfig.signupEntries.generate')}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  leftIcon={<FiDownload />}
                  onClick={() => void handleExport()}
                  isLoading={isExporting}
                >
                  {t('common.export.label')}
                </Button>
                <Button size="sm" variant="outline" leftIcon={<FiUpload />} onClick={openImportModal}>
                  {t('systemConfig.signupEntries.import')}
                </Button>
                <Button size="sm" colorScheme="admin" leftIcon={<FiPlus />} onClick={handleCreate}>
                  {t('systemConfig.signupEntries.add')}
                </Button>
              </HStack>
            </Flex>
          </CardBody>
        </Card>

        <Card bg={cardBg}>
          <CardHeader>
            <HStack justify="space-between">
              <Heading size="md">{t('systemConfig.signupEntries.list')}</Heading>
              {isInviteType ? (
                <Badge colorScheme="blue" variant="subtle">
                  <HStack spacing={1}>
                    <Text fontSize="xs">{t('systemConfig.signupEntries.codeLengthHint')}</Text>
                  </HStack>
                </Badge>
              ) : (
                <Badge colorScheme="purple" variant="subtle">
                  <HStack spacing={1}>
                    <FiMail />
                    <Text fontSize="xs">{t('systemConfig.signupEntries.whitelistHint')}</Text>
                  </HStack>
                </Badge>
              )}
            </HStack>
          </CardHeader>
          <CardBody pt={0}>
            <DataTable<MerchantSignupAccessEntry>
              data={rows}
              loading={isLoading}
              refreshing={isFetching}
              showRefresh
              onRefresh={() => refetch()}
              columns={columns}
              actions={actions}
              actionsColumnWidth="88px"
              showFilterBar={false}
              sortable={false}
              size="sm"
              emptyMessage={t('systemConfig.signupEntries.empty')}
            />
          </CardBody>
        </Card>
      </VStack>

      <Modal isOpen={isEditModalOpen} onClose={handleClose} size="lg">
        <ModalOverlay />
        <ModalContent className="system-config-dialog">
          <ModalHeader fontSize="lg" py={4}>
            {editingRow
              ? t('systemConfig.signupEntries.editTitle')
              : t('systemConfig.signupEntries.createTitle')}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={3} align="stretch">
              <FormControl>
                <FormLabel fontSize="sm" mb={1.5}>
                  {t('systemConfig.signupEntries.columns.entryType')}
                </FormLabel>
                <Input
                  size="sm"
                  value={entryTypeOptions.find(option => option.value === currentEntryType)?.label ?? currentEntryType}
                  isReadOnly
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel fontSize="sm" mb={1.5}>
                  {isInviteType
                    ? t('systemConfig.signupEntries.columns.inviteCode')
                    : t('systemConfig.signupEntries.columns.email')}
                </FormLabel>
                <Input
                  size="sm"
                  value={form.value}
                  maxLength={isInviteType ? 20 : undefined}
                  onChange={event =>
                    setForm(prev => ({
                      ...prev,
                      value: normalizeEntryInput(currentEntryType, event.target.value),
                    }))
                  }
                  placeholder={isInviteType
                    ? t('systemConfig.signupEntries.placeholders.inviteCode')
                    : t('systemConfig.signupEntries.placeholders.email')}
                />
              </FormControl>

              <FormControl>
                <FormLabel fontSize="sm" mb={1.5}>
                  {t('systemConfig.signupEntries.columns.label')}
                </FormLabel>
                <Input
                  size="sm"
                  value={form.label}
                  onChange={event =>
                    setForm(prev => ({
                      ...prev,
                      label: event.target.value,
                    }))
                  }
                  placeholder={t('systemConfig.signupEntries.placeholders.label')}
                />
              </FormControl>

              <FormControl>
                <FormLabel fontSize="sm" mb={1.5}>
                  {t('systemConfig.signupEntries.columns.status')}
                </FormLabel>
                <DropdownSelect
                  value={form.status}
                  options={modalStatusOptions}
                  onChange={value =>
                    setForm(prev => ({
                      ...prev,
                      status: value as 'active' | 'disabled',
                    }))
                  }
                  size="xs"
                />
              </FormControl>

              <FormControl>
                <FormLabel fontSize="sm" mb={1.5}>
                  {t('systemConfig.signupEntries.columns.expiresAt')}
                </FormLabel>
                <Input
                  size="sm"
                  type="datetime-local"
                  value={form.expiresAt}
                  onChange={event =>
                    setForm(prev => ({
                      ...prev,
                      expiresAt: event.target.value,
                    }))
                  }
                />
              </FormControl>
              {importResult && (
                <VStack
                  align="stretch"
                  spacing={1.5}
                  borderWidth="1px"
                  borderColor="gray.200"
                  borderRadius="md"
                  p={3}
                >
                  <Text fontSize="sm" fontWeight="semibold">
                    {t('systemConfig.signupEntries.importResultTitle')}
                  </Text>
                  <Text fontSize="xs" color="gray.500">
                    {t('systemConfig.signupEntries.importSummary', {
                      created: importResult.createdCount,
                      updated: importResult.updatedCount,
                      skipped: importResult.skippedCount,
                    })}
                  </Text>
                  {importResult.errors.length > 0 && (
                    <VStack align="stretch" spacing={1} maxH="180px" overflowY="auto">
                      {importResult.errors.map((error) => (
                        <Text key={`${error.lineNumber}-${error.value}`} fontSize="xs" color="red.500">
                          {t('systemConfig.signupEntries.importErrorLine', {
                            line: error.lineNumber,
                            value: error.value,
                            message: error.message,
                          })}
                        </Text>
                      ))}
                    </VStack>
                  )}
                </VStack>
              )}
            </VStack>
          </ModalBody>
          <ModalFooter pt={2}>
            <Button size="sm" variant="ghost" onClick={handleClose}>
              {t('common.cancel')}
            </Button>
            <Button size="sm" colorScheme="admin" ml={3} onClick={handleSave} isLoading={isSaving}>
              {t('common.save')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal isOpen={isGenerateModalOpen} onClose={handleCloseGenerate} size="lg">
        <ModalOverlay />
        <ModalContent className="system-config-dialog">
          <ModalHeader fontSize="lg" py={4}>
            {t('systemConfig.signupEntries.generateTitle')}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={3} align="stretch">
              <FormControl isRequired>
                <FormLabel fontSize="sm" mb={1.5}>
                  {t('systemConfig.signupEntries.generateCount')}
                </FormLabel>
                <Input
                  size="sm"
                  type="number"
                  min={1}
                  max={MAX_GENERATE_COUNT}
                  value={generateForm.count}
                  onChange={event =>
                    setGenerateForm(prev => ({
                      ...prev,
                      count: event.target.value,
                    }))
                  }
                />
                <Text mt={1} fontSize="xs" color="gray.500">
                  {t('systemConfig.signupEntries.generateCountHint', {
                    defaultCount: DEFAULT_GENERATE_COUNT,
                    max: MAX_GENERATE_COUNT,
                  })}
                </Text>
              </FormControl>

              {generateResult && (
                <Box borderWidth="1px" borderColor="gray.200" borderRadius="md" p={3}>
                  <VStack spacing={2.5} align="stretch">
                    <HStack justify="space-between" align="center">
                      <Text fontSize="sm" fontWeight="semibold">
                        {t('systemConfig.signupEntries.generateResultTitle')}
                      </Text>
                      <Badge colorScheme="blue" variant="subtle">
                        {generateResult.batchNo}
                      </Badge>
                    </HStack>
                    <Text fontSize="xs" color="gray.500">
                      {t('systemConfig.signupEntries.generateResultSummary', {
                        count: generateResult.createdCount,
                        batchNo: generateResult.batchNo,
                      })}
                    </Text>
                    <FormControl>
                      <FormLabel fontSize="sm" mb={1.5}>
                        {t('systemConfig.signupEntries.generatedCodes')}
                      </FormLabel>
                      <Textarea
                        size="sm"
                        minH="180px"
                        fontFamily="mono"
                        value={generateResult.codes.join('\n')}
                        isReadOnly
                      />
                    </FormControl>
                    <HStack spacing={2} justify="flex-end" flexWrap="wrap" rowGap={2}>
                      <Button
                        size="sm"
                        variant="outline"
                        leftIcon={<FiCopy />}
                        onClick={handleCopyGeneratedCodes}
                      >
                        {t('systemConfig.signupEntries.copyGeneratedCodes')}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        leftIcon={<FiDownload />}
                        onClick={handleDownloadGeneratedCodes}
                      >
                        {t('systemConfig.signupEntries.downloadGeneratedCodes')}
                      </Button>
                    </HStack>
                  </VStack>
                </Box>
              )}
            </VStack>
          </ModalBody>
          <ModalFooter pt={2}>
            <Button size="sm" variant="ghost" onClick={handleCloseGenerate}>
              {t('common.cancel')}
            </Button>
            <Button
              size="sm"
              colorScheme="admin"
              ml={3}
              onClick={() => void handleGenerate()}
              isLoading={generateMutation.isPending}
            >
              {t('systemConfig.signupEntries.generate')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal isOpen={isImportModalOpen} onClose={handleCloseImport} size="lg">
        <ModalOverlay />
        <ModalContent className="system-config-dialog">
          <ModalHeader fontSize="lg" py={4}>
            {t('systemConfig.signupEntries.importTitle')}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={3} align="stretch">
              <FormControl>
                <FormLabel fontSize="sm" mb={1.5}>
                  {t('systemConfig.signupEntries.columns.entryType')}
                </FormLabel>
                <Input
                  size="sm"
                  value={entryTypeOptions.find(option => option.value === currentEntryType)?.label ?? currentEntryType}
                  isReadOnly
                />
              </FormControl>
              <FormControl isRequired>
                <FormLabel fontSize="sm" mb={1.5}>
                  {t('systemConfig.signupEntries.importValues')}
                </FormLabel>
                <Textarea
                  size="sm"
                  minH="220px"
                  value={importForm.values}
                  onChange={event =>
                    setImportForm(prev => ({
                      ...prev,
                      values: event.target.value,
                    }))
                  }
                  placeholder={t('systemConfig.signupEntries.importPlaceholder', {
                    type: isInviteType
                      ? t('systemConfig.signupEntries.entryTypes.inviteCode')
                      : t('systemConfig.signupEntries.entryTypes.emailWhitelist'),
                  })}
                />
                <Text mt={1} fontSize="xs" color="gray.500">
                  {t('systemConfig.signupEntries.importHint')}
                </Text>
              </FormControl>
              <FormControl>
                <FormLabel fontSize="sm" mb={1.5}>
                  {t('systemConfig.signupEntries.columns.status')}
                </FormLabel>
                <DropdownSelect
                  value={importForm.status}
                  options={modalStatusOptions}
                  onChange={value =>
                    setImportForm(prev => ({
                      ...prev,
                      status: value as 'active' | 'disabled',
                    }))
                  }
                  size="xs"
                />
              </FormControl>
              <FormControl>
                <FormLabel fontSize="sm" mb={1.5}>
                  {t('systemConfig.signupEntries.columns.expiresAt')}
                </FormLabel>
                <Input
                  size="sm"
                  type="datetime-local"
                  value={importForm.expiresAt}
                  onChange={event =>
                    setImportForm(prev => ({
                      ...prev,
                      expiresAt: event.target.value,
                    }))
                  }
                />
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter pt={2}>
            <Button size="sm" variant="ghost" onClick={handleCloseImport}>
              {t('common.cancel')}
            </Button>
            <Button
              size="sm"
              colorScheme="admin"
              ml={3}
              onClick={handleImport}
              isLoading={importMutation.isPending}
            >
              {t('systemConfig.signupEntries.import')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  )
}
